import axios from 'axios'
import {
  getItemFromStorage,
  removeItemFromStorage,
  setDataInStorage,
} from 'utils/storage'
import {buildPayloadWithRewardfulAttribution} from 'utils/referral'

const proxy = process.env.REACT_APP_STAGING_API_URL
const TOKEN_ERROR_STATUSES = [400, 401, 406]
const TOKEN_ERROR_MESSAGES = ['token expired.', 'token not found.']
let refreshTokenPromise = null
let isAxiosInterceptorAttached = false

// Utility function to clear all user session data
const clearUserSession = () => {
  try {
    removeItemFromStorage({name: 'user'})
    removeItemFromStorage({name: 'accessToken'})
    removeItemFromStorage({name: 'userLoginData'})
    removeItemFromStorage({name: 'cartId'})
    removeItemFromStorage({name: 'storeCategoryId'})
    sessionStorage.clear()
    console.log('User session cleared due to invalid token data')
  } catch (error) {
    console.error('Error clearing user session:', error)
  }
}

const forceLogoutToLogin = () => {
  clearUserSession()
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login')
  }
}

const getCommonHeaders = () => ({
  language: 'en',
  platform: 3,
  currencysymbol: '$',
  currencycode: 'USD',
})

const shouldAttemptTokenRefresh = (error) => {
  const status = error?.response?.status
  const message = error?.response?.data?.message?.toLowerCase()
  const originalRequest = error?.config || {}
  const requestUrl = originalRequest?.url || ''

  const isGenerateTokenCall = requestUrl.includes('/v1/generateToken')
  const isTokenStatus = TOKEN_ERROR_STATUSES.includes(status)
  const isTokenMessage = TOKEN_ERROR_MESSAGES.includes(message)

  if (isGenerateTokenCall) {
    return false
  }

  if (status === 401) {
    return true
  }

  return isTokenStatus && isTokenMessage
}

const persistNewAccessToken = (tokenResponseData) => {
  const currentLoginData = getItemFromStorage({ name: 'userLoginData' }) || {}
  const currentToken = currentLoginData?.token || {}
  const refreshedData = tokenResponseData?.data || {}
  const newAccessToken = refreshedData?.accessToken

  if (!newAccessToken) {
    throw new Error('Missing access token in refresh response')
  }

  const updatedLoginData = {
    ...currentLoginData,
    token: {
      ...currentToken,
      accessToken: newAccessToken,
      accessExpireAt: refreshedData?.accessExpireAt || currentToken?.accessExpireAt,
      // Keep existing refresh token unless backend explicitly returns a new one.
      refreshToken: refreshedData?.refToken || currentToken?.refreshToken,
    },
  }

  setDataInStorage({ name: 'userLoginData', data: updatedLoginData })
  return newAccessToken
}

const refreshAccessToken = async () => {
  const loginData = getItemFromStorage({ name: 'userLoginData' }) || {}
  const accessToken = loginData?.token?.accessToken
  const refToken = loginData?.token?.refreshToken

  if (!accessToken || !refToken) {
    throw new Error('Missing accessToken or refToken')
  }

  const response = await axios(proxy + '/v1/generateToken', {
    method: 'POST',
    headers: getCommonHeaders(),
    data: {
      accessToken,
      refToken,
    },
  })

  return persistNewAccessToken(response?.data)
}

const attachAxiosAuthInterceptor = () => {
  if (isAxiosInterceptorAttached) {
    return
  }

  axios.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error?.config || {}

      if (
        !shouldAttemptTokenRefresh(error) ||
        originalRequest?._retry === true
      ) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        if (!refreshTokenPromise) {
          refreshTokenPromise = refreshAccessToken().finally(() => {
            refreshTokenPromise = null
          })
        }

        const refreshedAccessToken = await refreshTokenPromise
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = refreshedAccessToken

        return axios(originalRequest)
      } catch (refreshError) {
        forceLogoutToLogin()
        return Promise.reject(refreshError)
      }
    }
  )

  isAxiosInterceptorAttached = true
}

attachAxiosAuthInterceptor()

// Enhanced getToken function with validation and automatic session cleanup
export const getToken = () => {
  try {
    const tokenData = getItemFromStorage({name: 'userLoginData'})

    if (!tokenData) {
      return {}
    }

    // Validate token structure - check if it has expected properties
    if (typeof tokenData !== 'object' || tokenData === null) {
      console.warn('Invalid token data structure, clearing session')
      clearUserSession()
      return {}
    }

    // Check if token object has the expected structure
    if (tokenData.token && typeof tokenData.token === 'object') {
      // Validate that accessToken exists and is a string
      if (
        !tokenData.token.accessToken ||
        typeof tokenData.token.accessToken !== 'string'
      ) {
        console.warn('Invalid or missing accessToken, clearing session')
        clearUserSession()
        return {}
      }

      // Check if token looks like it might be corrupted (basic validation)
      if (
        tokenData.token.accessToken.length < 10 ||
        tokenData.token.accessToken.includes('invalid')
      ) {
        console.warn(
          'Token appears to be invalid or corrupted, clearing session'
        )
        clearUserSession()
        return {}
      }
    }

    return tokenData
  } catch (error) {
    console.error('Error retrieving token data, clearing session:', error)
    clearUserSession()
    return {}
  }
}

export const getAccessToken = () =>
  getItemFromStorage({name: 'accessToken'}) || {}

export const initializeUser = async (data) => {
  const response = await axios(proxy + '/v1/guest/signIn', {
    method: 'POST',
    data: data,
    headers: {
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    setDataInStorage({
      name: 'accessToken',
      data: response?.data,
    })
    return response.data
  }
  return null
}

export const registerUser = async (payloadData) => {
  const {data} = getAccessToken()
  const payloadWithAttribution =
    await buildPayloadWithRewardfulAttribution(payloadData)
  const response = await axios(proxy + '/v1/signUp', {
    method: 'POST',
    data: payloadWithAttribution,
    headers: {
      Authorization: data?.token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const loginUser = async (payloadData) => {
  const {data} = getAccessToken()
  const response = await axios(proxy + '/v1/signIn', {
    method: 'POST',
    headers: {
      Authorization: data?.token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    setDataInStorage({name: 'userLoginData', data: response.data?.data})
    return response.data
  }
  return null
}

export const logoutUser = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/customer/logout', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    removeItemFromStorage({name: 'user'})
    removeItemFromStorage({name: 'accessToken'})
    removeItemFromStorage({name: 'userLoginData'})
    removeItemFromStorage({name: 'cartId'})
    removeItemFromStorage({name: 'storeCategoryId'})
    sessionStorage.clear()
    return response.data
  }
  return null
}

export const verifyCode = async (payloadData) => {
  const {data} = getAccessToken()
  const response = await axios(proxy + '/v1/customer/verifyOtp', {
    method: 'POST',
    headers: {
      Authorization: data?.token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    setDataInStorage({name: 'userLoginData', data: response.data?.data})
    return response.data
  }
  return null
}

export const forgotPassword = async (payloadData) => {
  const {data} = getAccessToken()
  const response = await axios(proxy + '/v1/forgotPassword', {
    method: 'POST',
    headers: {
      Authorization: data?.token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getUserProfile = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/customer/profile?userId=${id}`, {
    method: 'GET',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    const {data} = response.data
    setDataInStorage({name: 'user', data: data})
    return {data}
  }
  return null
}

export const updateProfile = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/customer/profile`, {
    method: 'PATCH',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const uploadFile = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/imageUpload`, {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      'Content-Type': 'multipart/form-data',
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const deleteImage = async (data) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/imageDelete`, {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      'Content-Type': 'multipart/form-data',
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: data,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}

export const getVehicles = async (payloadData) => {
  const {token} = getToken()
  const {locations, time, pickupTimeZone} = payloadData || {}
  const response = await axios(proxy + '/v1/vehicleTypes/by-location', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: {
      locations,
      time,
      pickupTimeZone,
    },
  })
  if (response && response.data) {
    const {data, totalCount} = response.data
    return {data, totalCount}
  }
  return null
}

export const getOptionsForVehicle = async (id) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/vehicleTypes?vehicleTypeId=${id}`, {
    method: 'GET',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getContacts = async ({
  pageNumber = 1,
  size = 8,
  userId,
  isTeamMember,
  search,
  all,
}) => {
  const {token} = getToken()
  // Calculate skip as (pageNumber - 1) * size.
  // For pageNumber = 1, skip = 0; for pageNumber = 2, skip = 8; etc.
  const skip = (pageNumber - 1) * size

  const response = await axios(
    proxy +
      `/v1/contactToBuyerProfileSearch?skip=${skip}&limit=${all ? 100 : size}&contactPersonUserId=${userId}&status=1&isTeamMember=${isTeamMember}&search=${search}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const createContact = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/contactToBuyerProfile', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data, totalCount} = response.data
    return {data, totalCount}
  }
  return null
}

export const editContact = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/contactToBuyerProfile', {
    method: 'PATCH',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data, totalCount} = response.data
    return {data, totalCount}
  }
  return null
}

export const deleteContact = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/contactToBuyerProfile', {
    method: 'DELETE',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data, totalCount} = response.data
    return {data, totalCount}
  }
  return null
}

export const getContactLocations = async ({userId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/address?userId=${userId}&isRequestFrom=1&search=${search}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getAllContactLocations = async ({userId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/address?isRequestFrom=1&userId=${userId}&search=${search}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const createContactLocation = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/address', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const updateContactLocation = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/address', {
    method: 'PATCH',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getContact = async ({userId, id}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/contactToBuyerProfileById?userId=${userId}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const deleteContactLocation = async ({addressId}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/address?addressId=${addressId}`, {
    method: 'DELETE',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    const {data, totalCount} = response.data
    return {data, totalCount}
  }
  return null
}

export const createCart = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/cart', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    setDataInStorage({name: 'cartId', data: data?.cartId})
    return {data}
  }
  return null
}

export const completeOrder = async (payloadData) => {
  const {token} = getToken()
  const payloadWithAttribution =
    await buildPayloadWithRewardfulAttribution(payloadData)
  const response = await axios(proxy + '/v1/order/multipleDelivery', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadWithAttribution,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const initializeDefaultCommodity = async () => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      '/v1/commodities?isTowable=false&limit=1&skip=0&defaultCommodity=true',
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getStoreCategoryId = async ({cityId}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/storeCategory?cityId=${cityId}`, {
    method: 'GET',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    const {data} = response.data
    setDataInStorage({
      name: 'storeCategoryId',
      data: data[0]?.id,
    })
    return {data}
  }
  return null
}

export const getOpenLoadOrders = async ({storeCategoryId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders?storeCategoryId=${storeCategoryId}&limit=40&skip=0&requestFrom=1&status=5&cityId=&${search ? `search=${search}` : 'search='}&bookingAssign=2&groupByMaster=true&groupByParent=true&storeType=23`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getAssignedLoadOrders = async ({storeCategoryId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders?storeCategoryId=${storeCategoryId}&limit=20&skip=0&requestFrom=1&status=5&cityId=&${search ? `search=${search}` : 'search='}&bookingAssign=1&bookingStarted=1&groupByMaster=true&groupByParent=true&storeType=23`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getEnrouteToPickupOrders = async ({storeCategoryId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders?storeCategoryId=${storeCategoryId}&limit=20&skip=0&requestFrom=1&status=5&cityId=&${search ? `search=${search}` : 'search='}&bookingAssign=1&bookingStarted=2&groupByMaster=true&groupByParent=true&storeType=23`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getEnrouteToDeliveryOrders = async ({storeCategoryId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders?storeCategoryId=${storeCategoryId}&limit=20&skip=0&requestFrom=1&status=6&cityId=&${search ? `search=${search}` : 'search='}&groupByMaster=true&groupByParent=true&storeType=23`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getCompletedOrders = async ({
  userId,
  search,
  pageNumber = 1,
  size = 8,
}) => {
  const {token} = getToken()
  const skip = (pageNumber - 1) * size
  const response = await axios(
    proxy +
      `/v1/orders?userId=${userId}&limit=${size}&skip=${skip}&groupByMaster=true&groupByParent=true&requestFrom=1&status=7&cityId=&storeType=23&search=${search}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getCancelledOrders = async ({
  userId,
  search,
  pageNumber = 1,
  size = 8,
}) => {
  const {token} = getToken()
  const skip = (pageNumber - 1) * size

  const response = await axios(
    proxy +
      `/v1/orders?userId=${userId}&limit=${size}&skip=${skip}&groupByMaster=true&groupByParent=true&requestFrom=1&status=3&cityId=&storeType=23&search=${search}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getOrder = async ({packageId}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/order/packages?packageIds=${packageId}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getCart = async ({
  userId,
  storeCategoryId,
  cartId,
  isMultiCart,
  deliveryFeeCalculate = 1,
}) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/cart', {
    method: 'GET',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    params: {
      userId,
      storeCategoryId,
      isMultiCart: isMultiCart || 1,
      deliveryFeeCalculate,
      cartId,
    },
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const deleteCart = async ({cartId}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/cart?cartId=${cartId}`, {
    method: 'DELETE',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    removeItemFromStorage({name: 'cartId'})
    const {data} = response.data
    return {data}
  }
  return null
}

export const deleteOrder = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/order', {
    method: 'DELETE',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getWallet = async ({userId}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/wallet?userId=${userId}&userType=customer`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        lan: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const rechargeWallet = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/wallet/recharge', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getPaymentHistory = async ({walletId, paymentType}) => {
  //0 - all
  //1 - credit
  //2 - debit
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/walletTransaction?walletId=${walletId}&txnType=${paymentType}&fetchSize=20`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        lan: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getCreditCards = async ({userId}) => {
  const {token} = getToken()
  const response = await axios(proxy + `/stripe/v1/customer?userId=${userId}`, {
    method: 'GET',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      lan: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const initializePayment = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/stripe/v1/setupIntent`, {
    method: 'GET',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      lan: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const addCreditCard = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/stripe/v1/customer', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      lan: 'en',
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const createReferralStripeCustomer = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/stripe/v1/customer/referral', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      lan: 'en',
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const deleteCard = async ({paymentMethod}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/stripe/v1/paymentMethod?paymentMethod=${paymentMethod}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        lan: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getOrders = async ({
  status,
  startDate,
  endDate,
  pageSize,
  startPosition = 0,
  search,
  customerId,
}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders-simplified?status=101&startDate=2025-04-01T00:00:00Z&endDate=2025-04-15T23:59:59Z&pageSize=10&startPosition=0&search=pickup&customerId=67a078f494c42bcceee43278`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const saveRecentPlace = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/recentPlaces', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getRecentPlaces = async (userId) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/recentPlaces?customerId=${userId}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const changePassword = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/resetPassword', {
    method: 'POST',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const deleteAccount = async ({userId}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/customer?id=${userId}&deleteType=1`,
    {
      method: 'DELETE',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, totalCount} = response.data
    return {data, totalCount}
  }
  return null
}

export const getCancellationReasons = async () => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      '/v1/cancellationReason?serviceType=1&type=1&limit=20&skip=0&res_for=customer',
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getOrderStatus = async ({masterOrderId}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/order-status?masterOrderId=${masterOrderId}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getOrderSimplified = async ({masterOrderId}) => {
  const {token} = getToken()
  const response = await axios(
    proxy + `/v1/orders-simplified?masterOrderId=${masterOrderId}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const getOpenLoadOrdersSimplified = async ({userId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders-simplified?status=101&startDate=2014-04-01T00:00:00Z&endDate=2075-04-15T23:59:59Z&pageSize=100&startPosition=0&customerId=${userId}&search=${search ? search : ''}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getAssignedLoadOrdersSimplified = async ({userId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders-simplified?status=102&startDate=2014-04-01T00:00:00Z&endDate=2075-04-15T23:59:59Z&pageSize=100&startPosition=0&customerId=${userId}&search=${search ? search : ''}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getEnrouteToPickupOrdersSimplified = async ({userId, search}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders-simplified?status=103&startDate=2014-04-01T00:00:00Z&endDate=2075-04-15T23:59:59Z&pageSize=100&startPosition=0&customerId=${userId}&search=${search ? search : ''}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getEnrouteToDeliveryOrdersSimplified = async ({
  userId,
  search,
}) => {
  const {token} = getToken()
  const response = await axios(
    proxy +
      `/v1/orders-simplified?status=104&startDate=2014-04-01T00:00:00Z&endDate=2075-04-15T23:59:59Z&pageSize=100&startPosition=0&customerId=${userId}&search=${search ? search : ''}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getCompletedOrdersSimplified = async ({
  userId,
  search,
  pageNumber = 1,
  size = 8,
}) => {
  const {token} = getToken()
  const skip = (pageNumber - 1) * size
  const response = await axios(
    proxy +
      `/v1/orders-simplified?status=8&startDate=2014-04-01T00:00:00Z&endDate=2075-04-15T23:59:59Z&pageSize=${size}&startPosition=${skip}&limit=${size}&customerId=${userId}&search=${search ? search : ''}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getCancelledOrdersSimplified = async ({
  userId,
  search,
  pageNumber = 1,
  size = 8,
}) => {
  const {token} = getToken()
  const skip = (pageNumber - 1) * size
  const response = await axios(
    proxy +
      `/v1/orders-simplified?status=11&startDate=2014-04-01T00:00:00Z&endDate=2075-04-15T23:59:59Z&pageSize=${size}&startPosition=${skip}&limit=${size}&customerId=${userId}&search=${search ? search : ''}`,
    {
      method: 'GET',
      headers: {
        Authorization: token?.accessToken,
        language: 'en',
        platform: 3,
        currencysymbol: '$',
        currencycode: 'USD',
      },
    }
  )
  if (response && response.data) {
    const {data, count} = response.data
    return {data, count}
  }
  return null
}

export const getOrderValueOptions = async () => {
  const {token} = getToken()
  const response = await axios(proxy + `/v1/estimated-order-values`, {
    method: 'GET',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
  })
  if (response && response.data) {
    const {data} = response.data
    return {data}
  }
  return null
}

export const updateBillOfLading = async (payloadData) => {
  const {token} = getToken()
  const response = await axios(proxy + '/v1/order/billOfLading', {
    method: 'PATCH',
    headers: {
      Authorization: token?.accessToken,
      language: 'en',
      platform: 3,
      currencysymbol: '$',
      currencycode: 'USD',
    },
    data: payloadData,
  })
  if (response && response.data) {
    return response.data
  }
  return null
}
